import React from "react";
import { SvgIcon, SxProps, TextField, Theme } from "@mui/material";
import settingsShellTokens from "screens/Settings/Settings.tokens";

const { search } = settingsShellTokens;

interface SettingsSearchFieldProps {
  id?: string;
  placeholder?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.Ref<any>;
  width?: number | string;
  /**
   * Accessible name for the input. The design shows no visible label, and a
   * placeholder is not a label (WCAG 4.1.2 / 3.3.2) — it disappears on the
   * first keystroke — so the field is named here. Defaults to the placeholder.
   */
  ariaLabel?: string;
  /** Screen-level overrides (the design tints and resizes the in-card variant). */
  sx?: SxProps<Theme>;
}

/**
 * Search glyph traced from the Settings design — a thin stroked magnifier at
 * 13px, noticeably lighter than the shared 20px raster icon used elsewhere.
 */
const SearchGlyph = () => (
  <SvgIcon
    viewBox="0 0 24 24"
    fill="none"
    stroke={search.iconColor}
    strokeWidth={search.iconStrokeWidth}
    strokeLinecap="round"
    aria-hidden="true"
    sx={{
      width: search.iconSize,
      height: search.iconSize,
      flexShrink: 0,
      fill: "none",
    }}
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </SvgIcon>
);

/**
 * Compact search field for Settings list-screen headers (Roles, Teams,
 * Tickets). Geometry, colours and type come from the Settings shell tokens so
 * it reproduces the design's 40px / 9px-radius box exactly.
 */
const SettingsSearchField: React.FC<SettingsSearchFieldProps> = ({
  id,
  placeholder = "Search",
  onChange,
  inputRef,
  width = search.width,
  ariaLabel,
  sx,
}) => (
  <TextField
    id={id}
    inputRef={inputRef}
    placeholder={placeholder}
    onChange={onChange}
    variant="outlined"
    type="search"
    autoComplete="off"
    inputProps={{ "aria-label": ariaLabel ?? placeholder }}
    sx={[
      {
        // The tablet reference runs the search box full width too.
        width: { xs: "100%", md: width },
        "& .MuiOutlinedInput-root": {
          height: { xs: "44px", md: search.height },
          minHeight: { xs: "44px", md: search.height },
          px: search.paddingXCompensated,
          gap: search.gap,
          borderRadius: search.radius,
          backgroundColor: search.bg,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderWidth: search.borderWidth,
          borderColor: search.border,
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: search.border,
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderWidth: search.borderWidth,
            borderColor: search.focusBorder,
          },
        "& .MuiOutlinedInput-input": {
          py: 0,
          px: search.inputPaddingX,
          fontSize: search.fontSize,
          fontWeight: search.fontWeight,
          color: search.color,
          "&::placeholder": { color: search.placeholder, opacity: 1 },
        },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    InputProps={{ startAdornment: <SearchGlyph /> }}
  />
);

export default SettingsSearchField;
